import {
  FileInput,
  Text,
  Stack,
  List,
  Anchor,
  Alert,
  Tooltip,
  Group,
  ThemeIcon,
} from '@mantine/core';
// import { LoansContext } from '@/context/loans-context-definition'; // No longer needed for maritalStatus
// import { use } from 'react'; // No longer needed
import { useState } from 'react'; // For modal state, though the duplicate check might change
import { IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';

interface FileUploadProps {
  file: File | null;
  setFile: (file: File | null) => void;
  fileError: string | null;
  borrowerType: 'user' | 'partner';
  isLoading?: boolean; // Overall loading state for the app or specific file processing
  disabled?: boolean; // To disable input during processing
  // We might need a way to compare against the *other* borrower's file if strict duplicate prevention across borrowers is needed.
  // For now, this component is self-contained for one borrower context.
}

export function FileUpload({
  file,
  setFile,
  fileError,
  borrowerType,
  isLoading = false,
  disabled = false,
}: FileUploadProps) {
  // const [opened, { open, close }] = useDisclosure(false); // Removed useDisclosure
  const [isDuplicateWarning, setIsDuplicateWarning] = useState(false);

  function handleFileChange(newFile: File | null) {
    if (newFile && file && newFile.name === file.name && newFile.size === file.size) {
      // open(); // Removed open() call
      setIsDuplicateWarning(true);
      return;
    }
    setIsDuplicateWarning(false); // Clear local warning if a new, different file is chosen or cleared
    setFile(newFile); // Propagate change to parent
  }

  const label = 'Upload NSLDS file';
  const placeholder =
    borrowerType === 'user'
      ? 'Click to select your .txt file'
      : "Click to select partner's .txt file";

  const instructions = (
    <Stack gap="xs">
      <List size="sm">
        <List.Item>
          Log in at{' '}
          <Anchor href="https://studentaid.gov/fsa-id/sign-in/landing" target="_blank">
            studentaid.gov
          </Anchor>{' '}
          with your FSA ID
        </List.Item>
        <List.Item>Click on your name near the top, and select "My Aid"</List.Item>
        <List.Item>Click "Download My Aid Data"</List.Item>
        <List.Item>Save the file to your device</List.Item>
        <List.Item>Upload here to analyze your loans</List.Item>
      </List>
    </Stack>
  );

  return (
    <Stack>
      <FileInput
        labelProps={{ style: { display: 'flex', alignItems: 'center' } }} // Ensure label and icon align
        label={
          <Group gap="xs" wrap="nowrap">
            <Text component="span" size="sm" fw={500}>
              {label}
            </Text>
            <Tooltip
              label={instructions}
              multiline
              w={350}
              withArrow
              position="top-start"
              events={{ hover: true, focus: true, touch: true }}
            >
              <ThemeIcon variant="transparent" size="xs" style={{ cursor: 'pointer' }}>
                <IconInfoCircle />
              </ThemeIcon>
            </Tooltip>
          </Group>
        }
        placeholder={placeholder}
        accept=".txt"
        value={file} // Use the generic file prop
        onChange={handleFileChange} // Use the unified handler
        disabled={disabled || isLoading} // isLoading likely refers to the specific file processing via parent
        error={!!fileError || isDuplicateWarning} // Show error from props OR local duplicate warning
        clearable
      />
      {isDuplicateWarning && (
        <Alert
          title="Duplicate file"
          color="orange" // Or another appropriate color
          icon={<IconAlertCircle size={16} />}
          withCloseButton
          onClose={() => {
            setIsDuplicateWarning(false);
          }}
          mt="xs"
        >
          This file appears to be identical to the one already selected. Please choose a different
          file or clear the current selection.
        </Alert>
      )}
    </Stack>
  );
}
